import type { AiOperationsRun } from "@adpilot/shared";
import { IconChevronLeft } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ControlButton } from "@/components/patterns/control-button";
import { Badge } from "@/components/ui/badge";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { useFormatters } from "@/lib/format";
import { type DecisionStatus, formatRunCompletion } from "./decision-record-presentation";

export function DecisionRecordHeader({
  onOpenDetails,
  run,
  status,
  subtitle,
}: {
  onOpenDetails?: () => void;
  run: AiOperationsRun;
  status?: DecisionStatus;
  subtitle?: string;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { locale } = useFormatters();
  const campaignName = run.campaignName ?? t("runDetail.fallbackTitle");
  return (
    <header className="flex min-h-[74px] items-start justify-between border-b border-line pb-4">
      <div className="flex min-w-0 max-w-[720px] flex-col gap-[5px]">
        <p className="flex min-w-0 items-center gap-1 text-xs leading-4 text-muted-foreground">
          <Link
            to="/ai-operations"
            search={DEFAULT_APPROVAL_SEARCH}
            className="inline-flex shrink-0 items-center gap-0.5 font-medium transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <IconChevronLeft className="size-3.5" aria-hidden />
            {t("runDetail.back")}
          </Link>
          <span aria-hidden>/</span>
          <span className="truncate">{campaignName}</span>
        </p>
        <h1 className="text-2xl font-semibold leading-7 tracking-[-0.02em] text-ink">
          {t("runDetail.decisionRecord.title")}
        </h1>
        <p className="text-xs leading-4 text-muted-foreground">
          {subtitle ?? formatRunCompletion(run, t, locale)}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {onOpenDetails ? (
          <ControlButton controlSize="toolbar" variant="outline" onClick={onOpenDetails}>
            {t("runDetail.decisionRecord.runDetails")}
          </ControlButton>
        ) : null}
        {status ? <StatusPill status={status} /> : null}
      </div>
    </header>
  );
}

function StatusPill({ status }: { status: DecisionStatus }): React.ReactElement {
  return (
    <Badge
      variant={status.tone === "danger" ? "destructive" : status.tone}
      className="h-8 gap-[7px] px-[11px] text-xs font-semibold"
    >
      <span className="size-1.5 rounded-full bg-current" />
      {status.label}
    </Badge>
  );
}
